import { Goal, PathBreadcrumb } from '@/core/interfaces';
import { Injectable, signal } from '@angular/core';
import { HiddenToolPostBody } from 'src/server/schemas/common';
import { HiddenToolAttributes } from 'src/server/types';
import { HttpService } from './http.service';

@Injectable({
  providedIn: 'root',
})
export class ToolListService {
  successMetricsToolListSignal = signal<Goal[]>([]);
  successMetricsHelpToolListSignal = signal<Goal[]>([]);
  adminSupportToolListSignal = signal<Goal[]>([]);
  adminSupportHelpToolListSignal = signal<Goal[]>([]);
  externalAdminSupportToolListSignal = signal<Goal[]>([]);
  externalAdminSupportHelpToolListSignal = signal<Goal[]>([]);

  constructor(private httpService: HttpService) {}

  setSuccessMetricsToolList(hiddenTools: HiddenToolAttributes[]) {
    const getSuccessMetricsToolList = (forHelpPages = false): Goal[] => {
      let appendPath = '';
      if (forHelpPages) {
        appendPath = '-help-guide';
      }

      const toolList: Goal[] = [
        {
          title: `Student Profile`,
          image_key: 'homepageStudentCohortProfile',
          tool_URL: `/student-profile${appendPath}`,
          help_url: `/student-profile-help-guide`,
          tool_permission: 'student-profile',
          description: forHelpPages
            ? 'Contains information on how the Student Profile tool can be used.'
            : 'Displays information about a student.',
          logo_name: 'studentCohortProfile.svg',
          hidden: false,
        },
        {
          title: `Cohort Profile`,
          image_key: 'homepageStudentCohortProfile',
          tool_URL: `/cohort-profile${appendPath}`,
          help_url: `/cohort-profile-help-guide`,
          tool_permission: 'cohort-profile',
          description: forHelpPages
            ? 'Contains information on how the Cohort Profile tool can be used.'
            : 'Displays information about a cohort.',
          logo_name: 'studentCohortProfile.svg',
          hidden: false,
        },
        {
          title: `Advisor Identified At-Risk Students`,
          image_key: 'homepageAtRiskStudent',
          tool_URL: `/at-risk-students${appendPath}`,
          help_url: `/at-risk-students-help-guide`,
          tool_permission: 'at-risk-students',
          description: forHelpPages
            ? 'Contains steps on how the Advisor Identified At-Risk tool can be used.'
            : 'Displays a table of at-risk students that are identified by the advisor.',
          logo_name: 'atRiskStudent.svg',
          hidden: false,
        },
        {
          title: `Course Combinations`,
          image_key: 'homepageCourseCombinations',
          tool_URL: `/course-combinations${appendPath}`,
          help_url: `/course-combinations-help-guide`,
          tool_permission: 'course-combinations',
          description: forHelpPages
            ? 'Contains information on how the Course Combination tool can be used.'
            : 'Displays course combinations pass rate with different number of courses up to 5.',
          logo_name: 'courseCombinations.svg',
          hidden: false,
        },
        {
          title: `Prerequisite Impact`,
          image_key: 'homepagePrerequisiteImpact',
          tool_URL: `/prerequisite-impact${appendPath}`,
          help_url: `/prerequisite-impact-help-guide`,
          tool_permission: 'prerequisite-impact',
          description: forHelpPages
            ? 'Contains information on how the Prerequisite Impact tool can be used.'
            : 'Identifies all the prerequisite courses for a particular course.',
          logo_name: 'prerequisiteImpact.svg',
          hidden: false,
        },
        {
          title: `Program Bottleneck Courses`,
          image_key: 'homepageProgramBottleneck',
          tool_URL: `/program-bottleneck-courses${appendPath}`,
          help_url: `/program-bottleneck-courses-help-guide`,
          tool_permission: 'program-bottleneck-courses',
          description: forHelpPages
            ? 'Contains information on how the Program Bottleneck Courses tool can be used.'
            : 'Displays 5 courses with the lowest passing rates per program.',
          logo_name: 'programBottleneckCourses.svg',
          hidden: false,
        },
        {
          title: `Course Recommender`,
          image_key: 'homepageCourseRecommender',
          tool_URL: `/course-recommender${appendPath}`,
          help_url: `/course-recommender-help-guide`,
          tool_permission: 'course-recommender',
          description: forHelpPages
            ? 'Contains information on how the Course Recommender tool can be used.'
            : 'Displays top 10 course recommendations based on past performance.',
          logo_name: 'coursesRecommender.svg',
          hidden: false,
        },
        {
          title: `Course Grade Estimation`,
          image_key: 'homepageCourseGradeEstimation',
          tool_URL: `/course-grade-estimation${appendPath}`,
          help_url: `/course-grade-estimation-help-guide`,
          tool_permission: `course-grade-estimation`,
          description: forHelpPages
            ? 'Contains information on how the Course Grade Estimation tool can be used.'
            : 'Predict the course performance for a target course.',
          logo_name: 'courseGradeEstimation.svg',
          hidden: false,
        },
      ];

      return toolList;
    };

    const successMetricTools = getSuccessMetricsToolList();
    successMetricTools.forEach((tool) => {
      tool.hidden = hiddenTools.some((x) => x.tool_url === tool.tool_URL);
    });
    this.successMetricsToolListSignal.set(successMetricTools);
    const successMetricHelpTools = getSuccessMetricsToolList(true);
    successMetricHelpTools.forEach((tool) => {
      tool.hidden = hiddenTools.some((x) => x.tool_url === tool.tool_URL);
    });
    this.successMetricsHelpToolListSignal.set(successMetricHelpTools);
  }

  setEnrollmentRetentionToolList(hiddenTools: HiddenToolAttributes[]) {
    const getEnrollmentRetentionToolList = (forHelpPages = false): Goal[] => {
      let appendPath = '';
      if (forHelpPages) {
        appendPath = '-help-guide';
      }
      const toolList: Goal[] = [
        {
          title: `Enrollment Retention Calculator`,
          image_key: 'homepageEnrollmentRetentionTool',
          tool_URL: `/enrollment-retention-calculator${appendPath}`,
          help_url: `/enrollment-retention-calculator-help-guide`,
          tool_permission: 'enrollment-retention-calculator',
          description: forHelpPages
            ? 'Contains information on how the Enrollment Retention tool can be used.'
            : 'Allows you to increase or decrease enrollment for upcoming years.',
          logo_name: 'enrollmentRetentionTool.svg',
          hidden: false,
        },
        {
          title: `Enrollment Trend`,
          image_key: 'homepageEnrollmentTrend',
          tool_URL: `/enrollment-trend${appendPath}`,
          help_url: `/enrollment-trend-help-guide`,
          tool_permission: 'enrollment-trend',
          description: forHelpPages
            ? 'Contains information on how the Enrollment Trend tool can be used.'
            : 'Allows you to look at the historical and enrollment trend for past years.',
          logo_name: 'enrollmentTrend.svg',
          hidden: false,
        },
      ];

      return toolList;
    };
  }

  setAdminSupportToolList(hiddenTools: HiddenToolAttributes[]) {
    const getAdminSupportToolList = (forHelpPages = false): Goal[] => {
      let appendPath = '';
      if (forHelpPages) {
        appendPath = '-help-guide';
      }
      const toolList: Goal[] = [
        {
          title: `APR/Graduation/Summary`,
          image_key: 'homepageAprGraduationSummary',
          tool_URL: `/apr-graduation-summary${appendPath}`,
          help_url: `/apr-graduation-summary-help-guide`,
          tool_permission: 'apr-graduation-summary',
          description: forHelpPages
            ? 'Contains information on how the APR/Graduation/Summary tool can be used.'
            : 'View enrollment/retention, graduation time, first fall gpa, and apr based on chosen parameters.',
          logo_name: 'apr-graduation-summary.svg',
          hidden: false,
        },
        {
          title: `Entry or Exit Flow Graph`,
          image_key: 'homepageExitEntryFlowGraph',
          tool_URL: `/entry-exit-flow${appendPath}`,
          help_url: `/entry-exit-flow-help-guide`,
          tool_permission: 'entry-exit-flow',
          description: forHelpPages
            ? 'Contains information on how the Entry or Exit Flow Graph tool can be used.'
            : 'Analyze the entry and exit flow graph for each cohort with chosen parameters.',
          logo_name: 'exitEntryFlowGraph.svg',
          hidden: false,
        },
        {
          title: `FTIC Course DWF & APR`,
          image_key: 'homepageFticDwfApr',
          tool_URL: `/ftic-course-dwf-apr${appendPath}`,
          help_url: `/ftic-course-dwf-apr-help-guide`,
          tool_permission: 'ftic-course-dwf-apr',
          description: forHelpPages
            ? 'Contains information on how the FTIC Course DWF & APR tool can be used.'
            : 'View the most enrolled FTIC courses and course grades by program.',
          logo_name: 'fticCourseDWFAPR.svg',
          hidden: false,
        },
        {
          title: `Transfer Enrollment and Trends`,
          image_key: 'homepageTransferOverview',
          tool_URL: `/transfer-enrollment-trends${appendPath}`,
          help_url: `/transfer-enrollment-trends-help-guide`,
          tool_permission: 'transfer-enrollment-trends',
          description: forHelpPages
            ? 'Contains information on how Transfer Enrollment and Trends tool can be used.'
            : 'View enrollment trend, student data, and retention/graduation rates for transfer students.',
          logo_name: 'transferEnrollmentTrends.svg',
          hidden: false,
        },
      ];

      return toolList;
    };

    const adminSupportTools = getAdminSupportToolList();
    adminSupportTools.forEach((tool) => {
      tool.hidden = hiddenTools.some((x) => x.tool_url === tool.tool_URL);
    });
    this.adminSupportToolListSignal.set(adminSupportTools);
    const adminSupportHelpTools = getAdminSupportToolList(true);
    adminSupportHelpTools.forEach((tool) => {
      tool.hidden = hiddenTools.some((x) => x.tool_url === tool.tool_URL);
    });
    this.adminSupportHelpToolListSignal.set(adminSupportHelpTools);
  }

  setExternalAdminSupportToolList(hiddenTools: HiddenToolAttributes[]) {
    const getExternalAdminSupportToolList = (forHelpPages = false): Goal[] => {
      let appendPath = '';
      if (forHelpPages) {
        appendPath = '-help-guide';
      }
      const toolList: Goal[] = [
        {
          title: `APR/Graduation/Summary`,
          image_key: 'homepageAprGraduationSummary',
          tool_URL: forHelpPages
            ? `/apr-graduation-summary-external${appendPath}`
            : '&tabname=ftic_dashboard',
          help_url: `/apr-graduation-summary-external-help-guide`,
          tool_permission: '&tabname=ftic_dashboard',
          description: forHelpPages
            ? 'Contains information on how the APR/Graduation/Summary (External) tool can be used.'
            : 'View enrollment/retention, graduation time, first fall gpa, and apr based on chosen parameters.',
          logo_name: 'apr-graduation-summary.svg',
          hidden: false,
        },
        {
          title: `Entry or Exit Flow Graph`,
          image_key: 'homepageExitEntryFlowGraph',
          tool_URL: forHelpPages
            ? `/entry-exit-flow-external${appendPath}`
            : '&tabname=ftic_migration_flow',
          help_url: `/entry-exit-flow-external-help-guide`,
          tool_permission: '&tabname=ftic_migration_flow',
          description: forHelpPages
            ? 'Contains information on how the Entry or Exit Flow Graph (External) tool can be used.'
            : 'Analyze the entry and exit flow graph for each cohort with chosen parameters.',
          logo_name: 'exitEntryFlowGraph.svg',
          hidden: false,
        },
        {
          title: `FTIC Course DWF & APR`,
          image_key: 'homepageFticDwfApr',
          tool_URL: forHelpPages
            ? `/ftic-course-dwf-apr-external${appendPath}`
            : '&tabname=ftic_course_dfw_apr',
          help_url: `/ftic-course-dwf-apr-external-help-guide`,
          tool_permission: '&tabname=ftic_course_dfw_apr',
          description: forHelpPages
            ? 'Contains information on how the FTIC Course DWF & APR (External) tool can be used.'
            : 'View the most enrolled FTIC courses and course grades by program.',
          logo_name: 'fticCourseDWFAPR.svg',
          hidden: false,
        },
        {
          title: `New Applicants`,
          image_key: 'homepageNewApplicants',
          tool_URL: forHelpPages
            ? `/new-applicants${appendPath}`
            : '&tabname=ftic_new_applicants',
          help_url: `/new-applicants-help-guide`,
          tool_permission: '&tabname=ftic_new_applicants',
          description: forHelpPages
            ? 'Contains information on how the New Applicants (External) tool can be used.'
            : 'View new applicants data preview by uploading a csv file.',
          logo_name: 'newApplicants.svg',
          hidden: false,
        },
        {
          title: `Three-Year Graduation Rate`,
          image_key: 'homepageExploreTierCharacteristics',
          tool_URL: forHelpPages
            ? `/three-year-graduation-rate${appendPath}`
            : '&tabname=transfer_ontime',
          help_url: `/three-year-graduation-rate-help-guide`,
          tool_permission: '&tabname=transfer_ontime',
          description: forHelpPages
            ? 'Contains information on how Three-Year Graduation Rate (External) tool can be used.'
            : 'Predictions of on-time graduation for transfer students.',
          logo_name: 'exploreTierCharacteristics.svg', //change logo
          hidden: false,
        },
        {
          title: `Transfer Enrollment and Trends`,
          image_key: 'homepageTransferOverview',
          tool_URL: forHelpPages
            ? `/transfer-enrollment-trends${appendPath}`
            : '&tabname=transfer_dashboard',
          help_url: `/transfer-enrollment-trends-help-guide`,
          tool_permission: '&tabname=transfer_dashboard',
          description: forHelpPages
            ? 'Contains information on how Transfer Enrollment and Trends (External) tool can be used.'
            : 'View enrollment trend, student data, and retention/graduation rates for transfer student.',
          logo_name: 'transferEnrollmentTrends.svg',
          hidden: false,
        },
        {
          title: `Critical Course Score`,
          image_key: 'homepageCriticalCourseScore',
          tool_URL: forHelpPages
            ? `/critical-course-score${appendPath}`
            : '&tabname=ftic_critical_course_score',
          help_url: `/critical-course-score-help-guide`,
          tool_permission: '&tabname=ftic_critical_course_score',
          description: forHelpPages
            ? 'Contains information on how Critical Course Score (External) tool can be used.'
            : 'Figure out when is the best time to take a selected course.',
          logo_name: 'criticalCourseScore.svg',
          hidden: false,
        },
      ];

      return toolList;
    };

    const externalAdminSupportTools = getExternalAdminSupportToolList();
    externalAdminSupportTools.forEach((tool) => {
      tool.hidden = hiddenTools.some((x) => x.tool_url === tool.tool_URL);
    });
    this.externalAdminSupportToolListSignal.set(externalAdminSupportTools);
    const externalAdminSupportHelpTools = getExternalAdminSupportToolList(true);
    externalAdminSupportHelpTools.forEach((tool) => {
      tool.hidden = hiddenTools.some((x) => x.tool_url === tool.tool_URL);
    });
    this.externalAdminSupportHelpToolListSignal.set(
      externalAdminSupportHelpTools,
    );
  }

  getHelpHomepageCards(): Goal[] {
    const toolList: Goal[] = [
      {
        title: `Using the Tools`,
        image_key: '',
        tool_URL: `/tools-support`,
        tool_permission: 'apr-graduation-summary',
        help_url: '',
        description: `The Using the Tools page explains how to fully utilize and operate the dashboard tools.`,
        logo_name: 'screwdriver-wrench-solid.svg',
        hidden: false,
      },
      {
        title: `Navigation Support`,
        image_key: '',
        tool_URL: `/navigation-support`,
        help_url: '',
        tool_permission: 'apr-graduation-summary',
        description: `The Navigation Support explains how to navigate the dashboard from the homepage to each tool's page.`,
        logo_name: 'navigate-icon.svg',
        hidden: false,
      },
      {
        title: `Glossary of Terms`,
        image_key: '',
        tool_URL: `/glossary-of-terms`,
        help_url: '',
        tool_permission: 'apr-graduation-summary',
        description: `The Glossary of Terms page contains definitions of all the terms that
            are used in the dashboard.`,
        logo_name: 'book-solid.svg',
        hidden: false,
      },
    ];

    return toolList;
  }

  getSuccessMetricsRelatedLinkBasedOnTool(
    toolTitle: string,
    forHelpPages = false,
  ): Goal[] {
    if (forHelpPages) {
      return this.successMetricsHelpToolListSignal().filter(
        (tool) => tool.title !== toolTitle,
      );
    }

    return this.successMetricsToolListSignal().filter(
      (tool) => tool.title !== toolTitle,
    );
  }

  getAdminSupportRelatedLinkBasedOnTool(
    toolTitle: string,
    forHelpPages = false,
  ): Goal[] {
    if (forHelpPages) {
      return this.adminSupportHelpToolListSignal().filter(
        (tool) => tool.title !== toolTitle,
      );
    }
    return this.adminSupportToolListSignal().filter(
      (tool) => tool.title !== toolTitle,
    );
  }

  getExternalAdminSupportRelatedLinkBasedOnTool(
    toolTitle: string,
    forHelpPages = false,
  ): Goal[] {
    if (forHelpPages) {
      return this.externalAdminSupportHelpToolListSignal().filter(
        (tool) => tool.title !== toolTitle,
      );
    }

    return this.externalAdminSupportToolListSignal().filter(
      (tool) => tool.title !== toolTitle,
    );
  }

  getHelpHomepageRelatedLinkBasedOnCard(cardTitle: string): Goal[] {
    return this.getHelpHomepageCards().filter(
      (tool) => tool.title !== cardTitle,
    );
  }

  getHelpPagePathBreadcrumbs(toolHelpPageToInclude: string) {
    const breadcrumbs: PathBreadcrumb[] = [
      {
        text: 'Help Homepage',
        path: '/help-page',
      },
      {
        text: 'Using the Tools',
        path: '/tools-support',
      },
    ];

    const toolPathLookup: { [tool_key: string]: PathBreadcrumb } = {
      student_profile: {
        text: 'Student Profile',
        path: '/student-profile-help-guide',
      },
      cohort_profile: {
        text: 'Cohort Profile',
        path: '/cohort-profile-help-guide',
      },
      at_risk: {
        text: 'Advisor Identified At-Risk Students',
        path: '/at-risk-students-help-guide',
      },
      course_combinations: {
        text: 'Course Combinations',
        path: '/course-combinations-help-guide',
      },
      prerequisite_impact: {
        text: 'Prerequisite Impact',
        path: '/prerequisite-impact-help-guide',
      },
      program_bottleneck_courses: {
        text: 'Program Bottleneck Courses',
        path: '/program-bottleneck-courses-help-guide',
      },
      course_recommender: {
        text: 'Course Recommender',
        path: '/course-recommender-help-guide',
      },
      course_grade_estimation: {
        text: 'Course Grade Estimation',
        path: 'course-grade-estimation-help-guide',
      },
      apr_graduation_summary: {
        text: 'APR/Graduation/Summary',
        path: '/apr-graduation-summary-help-guide',
      },
      entry_exit: {
        text: 'Entry or Exit Flow Graph',
        path: '/entry-exit-flow-help-guide',
      },
      ftic_course: {
        text: 'FTIC Course DWF & APR',
        path: '/ftic-course-dwf-apr-help-guide',
      },
      transfer_students_trends: {
        text: 'Transfer Enrollment and Trends',
        path: '/transfer-enrollment-trends-help-guide',
      },
      apr_graduation_summary_external: {
        text: 'APR/Graduation/Summary',
        path: '/apr-graduation-summary-external-help-guide',
      },
      entry_exit_external: {
        text: 'Entry or Exit Flow Graph',
        path: '/entry-exit-flow-external-help-guide',
      },
      ftic_course_external: {
        text: 'FTIC Course DWF & APR',
        path: '/ftic-course-dwf-apr-external-help-guide',
      },
      new_applicants: {
        text: 'New Applicants',
        path: '/new-applicants-help-guide',
      },
      three_year_grad: {
        text: 'Three Year Graduation Rate',
        path: '/three-year-graduation-rate-help-guide',
      },
      transfer_enrollment_trends: {
        text: 'Transfer Enrollment and Trends',
        path: '/transfer-enrollment-trends-help-guide',
      },
      critical_course_score: {
        text: 'Critical Course Score',
        path: '/critical-course-score-help-guide',
      },
    };

    breadcrumbs.push(toolPathLookup[toolHelpPageToInclude]);

    return breadcrumbs;
  }

  /** Hides the tool based on the tool URL since it's unique for each tool */
  setToolHiddenFlag({ title, tool_URL }: Goal, hiddenFlag: boolean) {
    this.updateHiddenTool({
      hidden: hiddenFlag,
      tool_title: title,
      tool_url: tool_URL,
    });

    let tool = this.successMetricsToolListSignal().find(
      (tool) => tool.tool_URL === tool_URL,
    );

    if (tool) {
      tool.hidden = hiddenFlag;
      this.successMetricsToolListSignal.set(
        this.successMetricsToolListSignal(),
      );
      return;
    }

    tool = this.successMetricsHelpToolListSignal().find(
      (tool) => tool.tool_URL === tool_URL,
    );

    if (tool) {
      tool.hidden = hiddenFlag;
      this.successMetricsHelpToolListSignal.set(
        this.successMetricsHelpToolListSignal(),
      );
      return;
    }

    tool = this.adminSupportToolListSignal().find(
      (tool) => tool.tool_URL === tool_URL,
    );

    if (tool) {
      tool.hidden = hiddenFlag;
      this.adminSupportToolListSignal.set(this.adminSupportToolListSignal());
      return;
    }

    tool = this.adminSupportHelpToolListSignal().find(
      (tool) => tool.tool_URL === tool_URL,
    );

    if (tool) {
      tool.hidden = hiddenFlag;
      this.adminSupportHelpToolListSignal.set(
        this.adminSupportHelpToolListSignal(),
      );
      return;
    }

    tool = this.externalAdminSupportToolListSignal().find(
      (tool) => tool.tool_URL === tool_URL,
    );

    if (tool) {
      tool.hidden = hiddenFlag;
      this.externalAdminSupportToolListSignal.set(
        this.externalAdminSupportToolListSignal(),
      );
      return;
    }

    tool = this.externalAdminSupportHelpToolListSignal().find(
      (tool) => tool.tool_URL === tool_URL,
    );

    if (tool) {
      tool.hidden = hiddenFlag;
      this.externalAdminSupportHelpToolListSignal.set(
        this.externalAdminSupportHelpToolListSignal(),
      );
    }
  }

  updateHiddenTool(payloadBody: HiddenToolPostBody) {
    this.httpService.post(`/hidden-tools`, payloadBody).subscribe();
  }
}
